/**
 * Corporate contacts sync manager for shared mailbox contacts
 */

import { BaseSyncManager } from './BaseSyncManager';
import { GraphApiClient } from './GraphApiClient';
import { SyncOptions, SyncType, DeltaResponse } from './types';

export class CorporateSyncManager extends BaseSyncManager {
  private isOutlookContext: boolean;

  constructor(isOutlookContext: boolean = true) {
    super(isOutlookContext);
    this.isOutlookContext = isOutlookContext;
  }

  protected getSyncType(): SyncType {
    return 'corporate';
  }

  protected getMetaKey(): string {
    return '';
  }

  protected async fetchDataDelta(accessToken: string, deltaToken: string, sharedUpn: string): Promise<any> {
    return GraphApiClient.fetchContactsDelta(accessToken, sharedUpn, deltaToken);
  }

  protected async fetchDataPage(accessToken: string, skipToken: string, top: number, sharedUpn: string): Promise<any> {
    return GraphApiClient.fetchContactsPage(accessToken, sharedUpn, skipToken, top);
  }

  protected async processDeltaQuery(accessToken: string, deltaToken: string, sharedUpn: string): Promise<DeltaResponse> {
    console.log('Processing delta query to get final delta token...');
    
    let currentDeltaToken = deltaToken;
    let nextLink: string | null = null;
    let finalDeltaToken: string | null = null;
    let pageCount = 0;
    
    do {
      pageCount++;
      console.log(`Processing delta query page ${pageCount}...`);
      
      const response = await this.fetchDataDelta(accessToken, currentDeltaToken, sharedUpn);
      
      // Update next link and delta token
      nextLink = response['@odata.nextLink'];
      finalDeltaToken = response['@odata.deltaLink'];
      
      console.log(`Page ${pageCount} response:`, {
        hasNextLink: !!nextLink,
        hasDeltaLink: !!finalDeltaToken,
        contactCount: response.value?.length || 0
      });
      
      // If we have a next link, use it for the next iteration
      if (nextLink) {
        currentDeltaToken = nextLink;
      }
      
      // Safety check to prevent infinite loops
      if (pageCount > 50) {
        console.warn('Delta query processing stopped after 50 pages to prevent infinite loop');
        break;
      }
      
      // Add a small delay between pages to prevent overwhelming the API
      if (nextLink) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
    } while (nextLink);
    
    console.log(`Delta query processing complete after ${pageCount} pages`);
    return { deltaToken: finalDeltaToken };
  }

  protected getSourceType(): string {
    return 'corporate';
  }

  /**
   * Filter contacts to only include those with email addresses (Outlook only)
   */
  private filterValidContacts(contacts: any[]): any[] {
    // Only filter for Outlook context, not Teams
    if (!this.isOutlookContext) {
      return contacts;
    }
    
    return contacts.filter((contact: any) => {
      const email = contact.emailAddresses?.[0]?.address;
      return email && email.trim() !== '';
    });
  }

  /**
   * Bootstrap sync - initial full download of all contacts using delta query
   */
  async bootstrapSync(
    accessToken: string, 
    sharedUpn: string, 
    options: SyncOptions = {}
  ): Promise<void> {
    if (this.isSyncing && !options.skipSearchIndex) {
      throw new Error('Sync already in progress');
    }

    this.isSyncing = true;
    const { onProgress, onError, batchSize = 500 } = options;

    // Set a timeout to prevent sync from getting stuck
    this.setSyncTimeout(5 * 60 * 1000, () => {
      onError?.(new Error('Bootstrap sync timeout'));
    });

    try {
      onProgress?.({
        current: 0,
        total: 0,
        phase: 'bootstrap',
        message: 'Starting bootstrap sync...'
      });

      // Check if bootstrap is already complete
      const meta = await this.store.getMeta();
      if (meta.bootstrapComplete) {
        onProgress?.({
          current: 1,
          total: 1,
          phase: 'bootstrap',
          message: 'Bootstrap already complete, skipping...'
        });
        return;
      }

      // Use fast pagination for initial sync, then get delta token
      console.log('Starting fast bootstrap sync...');
      const initialResponse = await this.fetchDataPage(accessToken, '', 999, sharedUpn);
      
      let processedContacts = 0;
      let nextLink = initialResponse['@odata.nextLink'];
      let deltaLink = initialResponse['@odata.deltaLink'];
      
      console.log('Initial response:', {
        hasNextLink: !!nextLink,
        hasDeltaLink: !!deltaLink,
        contactCount: initialResponse.value?.length || 0
      });

      onProgress?.({
        current: 0,
        total: 0, // We don't know total yet
        phase: 'bootstrap',
        message: 'Starting bootstrap sync...'
      });

      // Process initial batch - filter for contacts with email addresses
      const initialContacts = this.filterValidContacts(initialResponse.value || []);
      console.log(`Corporate contacts after filtering: ${initialContacts.length} out of ${initialResponse.value?.length || 0}`);
      const normalizedContacts = await this.normalizer.normalizeContacts(initialContacts, sharedUpn, 'corporate');
      
      await this.store.bulkUpsertContacts(normalizedContacts);
      processedContacts += initialContacts.length;

      onProgress?.({
        current: processedContacts,
        total: 0, // Still unknown
        phase: 'bootstrap',
        message: `Processed ${processedContacts} contacts so far...`
      });

      // Process remaining pages
      while (nextLink) {
        const response = await GraphApiClient.fetchContactsPageFromUrl(accessToken, nextLink);
        const contacts = this.filterValidContacts(response.value || []);
        
        if (contacts.length > 0) {
          const normalizedContacts = await this.normalizer.normalizeContacts(contacts, sharedUpn, 'corporate');
          
          await this.store.bulkUpsertContacts(normalizedContacts);
          processedContacts += contacts.length;

          onProgress?.({
            current: processedContacts,
            total: 0, // Unknown total
            phase: 'bootstrap',
            message: `Processed ${processedContacts} contacts so far...`
          });
        }

        nextLink = response['@odata.nextLink'];
        deltaLink = response['@odata.deltaLink'];
      }

      // Get delta token for future incremental syncs (skip if requested)
      let finalDeltaToken = deltaLink;
      console.log('Corporate sync delta token options:', { 
        hasDeltaLink: !!deltaLink, 
        skipDeltaToken: options.skipDeltaToken,
        willSkip: !deltaLink && options.skipDeltaToken
      });
      
      if (!finalDeltaToken && !options.skipDeltaToken) {
        console.log('Getting delta token for future incremental syncs...');
        try {
          // Process delta query to get the final delta token (no progress updates)
          const deltaResponse = await this.processDeltaQuery(accessToken, '', sharedUpn);
          finalDeltaToken = deltaResponse.deltaToken;
          console.log('Delta token obtained:', finalDeltaToken ? 'Yes' : 'No');
          if (finalDeltaToken) {
            console.log('Delta token preview:', finalDeltaToken.substring(0, 100) + '...');
          }
        } catch (error) {
          console.warn('Failed to get delta token:', error);
        }
      } else if (finalDeltaToken) {
        console.log('Using existing delta link from bootstrap sync');
      } else if (options.skipDeltaToken) {
        console.log('Skipping delta token generation (will be done in background)');
      }

      // Update meta with bootstrap completion
      await this.markBootstrapComplete(processedContacts, finalDeltaToken);
      
      // Log the delta token for debugging
      if (finalDeltaToken) {
        console.log('Bootstrap sync completed with delta token:', finalDeltaToken.substring(0, 100) + '...');
      } else {
        console.warn('Bootstrap sync completed but no delta token was obtained');
      }

      // Build search index after bootstrap sync
      await this.buildSearchIndex(options);

      // Only show progress if not skipping search index (i.e., not in parallel mode)
      if (!options.skipSearchIndex) {
        onProgress?.({
          current: processedContacts,
          total: processedContacts,
          phase: 'bootstrap',
          message: `Bootstrap sync complete! ${processedContacts} contacts synced`
        });
      }

    } catch (error) {
      const err = error instanceof Error ? error : new Error('Bootstrap sync failed');
      console.error('Bootstrap sync failed:', err);
      onError?.(err);
      throw err;
    } finally {
      this.isSyncing = false;
      this.clearSyncTimeout();
    }
  }

  /**
   * Incremental sync using delta tokens
   */
  async incrementalSync(
    accessToken: string,
    sharedUpn: string,
    options: SyncOptions = {}
  ): Promise<void> {
    if (this.isSyncing && !options.skipSearchIndex) {
      throw new Error('Sync already in progress');
    }

    this.isSyncing = true;
    const { onProgress, onError } = options;

    try {
      const meta = await this.store.getMeta();
      
      if (!meta.bootstrapComplete) {
        throw new Error('Bootstrap sync must be completed before incremental sync');
      }

      if (!meta.deltaToken) {
        console.warn('No delta token available for incremental sync; attempting regeneration...');
        try {
          await this.generateDeltaToken(accessToken, sharedUpn);
        } catch (e) {
          console.warn('Delta token regeneration failed:', e);
        }
        const refreshed = await this.store.getMeta();
        if (!refreshed.deltaToken) {
          console.error('Delta token still missing after regeneration; skipping incremental run');
          return;
        }
      }

      onProgress?.({
        current: 0,
        total: 0,
        phase: 'incremental',
        message: 'Starting incremental sync...'
      });

      // Fetch changes using delta token
      console.log('Starting incremental sync with delta token:', meta.deltaToken?.substring(0, 50) + '...');
      
      let response;
      try {
        response = await this.fetchDataDelta(accessToken, (await this.store.getMeta()).deltaToken as string, sharedUpn);
      } catch (error) {
        console.error('Delta sync failed; clearing token and attempting regeneration:', error);
        await this.clearInvalidDeltaToken();
        try {
          await this.generateDeltaToken(accessToken, sharedUpn);
        } catch {}
        const refreshed = await this.store.getMeta();
        if (!refreshed.deltaToken) {
          console.error('Could not recover delta token; skipping incremental run');
          return;
        }
        response = await this.fetchDataDelta(accessToken, refreshed.deltaToken, sharedUpn);
      }
      const contacts = response.value || [];
      const newDeltaLink = response['@odata.deltaLink'];
      
      console.log('Incremental delta response:', {
        hasNewDeltaLink: !!newDeltaLink,
        contactCount: contacts.length,
        hasChanges: contacts.length > 0
      });

      let addedCount = 0;
      let updatedCount = 0;
      let deletedCount = 0;

      // Separate contacts into batches for efficient processing
      const contactsToUpsert: any[] = [];
      const contactsToDelete: string[] = [];

      // Process changes in batches - filter for contacts with email addresses (Outlook only)
      for (const contact of contacts) {
        if (contact['@removed']) {
          contactsToDelete.push(contact.id);
          deletedCount++;
        } else {
          // Apply filtering only for Outlook context
          if (this.isOutlookContext) {
            const email = contact.emailAddresses?.[0]?.address;
            if (email && email.trim() !== '') {
              contactsToUpsert.push(contact);
            }
          } else {
            // For Teams, include all contacts
            contactsToUpsert.push(contact);
          }
        }
      }

      // Batch process deletions
      if (contactsToDelete.length > 0) {
        await this.store.bulkMarkContactsDeleted(contactsToDelete);
      }

      // Batch process upserts
      if (contactsToUpsert.length > 0) {
        const normalizedContacts = await this.normalizer.normalizeContacts(contactsToUpsert, sharedUpn, 'corporate');
        
        // Check which contacts are new vs updates
        const existingContacts = await Promise.all(
          normalizedContacts.map(contact => this.store.getContact(contact.id))
        );
        
        existingContacts.forEach((existing, index) => {
          if (existing) {
            updatedCount++;
          } else {
            addedCount++;
          }
        });

        await this.store.bulkUpsertContacts(normalizedContacts);
      }

      // Update meta with new delta token and sync time
      await this.updateIncrementalSyncMeta(newDeltaLink, addedCount, deletedCount);
      
      // Rebuild search index if contacts were added, updated, or deleted
      if (addedCount > 0 || updatedCount > 0 || deletedCount > 0) {
        await this.buildSearchIndex(options);
      }
      
      // Log the delta token for debugging
      if (newDeltaLink) {
        console.log('Incremental sync completed with new delta token:', newDeltaLink.substring(0, 100) + '...');
      } else {
        console.warn('Incremental sync completed but no new delta token was received, keeping existing one');
      }

      onProgress?.({
        current: contacts.length,
        total: contacts.length,
        phase: 'incremental',
        message: `Incremental sync complete! +${addedCount} -${deletedCount} ~${updatedCount}`
      });

    } catch (error) {
      const err = error instanceof Error ? error : new Error('Incremental sync failed');
      console.error('Incremental sync failed:', err);
      onError?.(err);
      throw err;
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Generate delta token for future incremental syncs
   */
  async generateDeltaToken(accessToken: string, sharedUpn: string): Promise<void> {
    console.log('Generating corporate delta token...');
    try {
      const meta = await this.store.getMeta();
      let deltaResponse;
      if (meta.deltaToken) {
        // Use existing delta token to get a fresh one (no contact download)
        console.log('Refreshing existing corporate delta token...');
        deltaResponse = await this.processDeltaQuery(accessToken, meta.deltaToken, sharedUpn);
      } else {
        // Generate initial delta token (no contact download)
        console.log('Generating initial corporate delta token...');
        deltaResponse = await this.processDeltaQuery(accessToken, '', sharedUpn);
      }
      if (deltaResponse.deltaToken) {
        // Re-read metadata to get latest state including Personal/Internal delta tokens
        const latestMeta = await this.store.getMeta();
        
        // Update meta with the new delta token and ensure bootstrap is complete
        await this.store.setMeta({
          ...latestMeta,  // Use latest metadata instead of stale meta
          deltaToken: deltaResponse.deltaToken,
          bootstrapComplete: true // Ensure bootstrap is marked complete
        });
        console.log('Corporate delta token generated successfully');
      }
    } catch (error) {
      console.warn('Failed to generate corporate delta token:', error);
    }
  }
}
